//Register.js
import Lottie from "lottie-react";
import loadingAnimation from "../tools/Animations/Loading Dots Blue.json";
import { trackEvent } from "../firebase/analytics";
import { useState } from "react";
import { registerUser } from "../firebase/auth";
import { useNavigate } from "react-router-dom";
import "../styles/Register.css"; // Import CSS file for styling
import Oiiko from "../tools/oiikotranslogo2.png";
import { parsePhoneNumberFromString } from "libphonenumber-js";

// 🔹 NEW: shared error UI + formatter
import StatusBanner from "../components/StatusBanner";
import { formatError } from "../utils/errorMessages";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [companyRole, setCompanyRole] = useState("");
  const [referralSource, setReferralSource] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // NEW: terms consent
  const [agreed, setAgreed] = useState(false);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // 🔹 GLOBAL error banner (object: { title, message })
  const [error, setError] = useState(null);

  // Email shape (basic)
  const isValidEmail = (em) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((em || "").trim());

  // Phone -> E.164 using libphonenumber-js (default US; swap to your needs)
  const normalizePhone = (raw, defaultCountry = "US") => {
    try {
      const phone = parsePhoneNumberFromString(
        String(raw || ""),
        defaultCountry,
      );
      if (phone && phone.isValid())
        return {
          ok: true,
          e164: phone.number,
          national: phone.formatNational(),
        };
    } catch (e) {}
    return {
      ok: false,
      reason: "Enter a valid phone (e.g., (555) 555-1234 or +1 555-555-1234).",
    };
  };

  // Password policy
  const passwordIssues = (pw, name, email) => {
    const issues = [];
    if ((pw || "").length < 8) issues.push("≥ 8 chars");
    if (!/[A-Z]/.test(pw)) issues.push("1 uppercase");
    if (!/[a-z]/.test(pw)) issues.push("1 lowercase");
    if (!/\d/.test(pw)) issues.push("1 number");
    if (!/[^A-Za-z0-9]/.test(pw)) issues.push("1 special");
    // avoid including obvious identifiers
    const local = (email || "").split("@")[0]?.toLowerCase();
    const tokens = (name || "")
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);
    const low = (pw || "").toLowerCase();
    if (local && low.includes(local))
      issues.push("cannot contain your email name");
    if (tokens.some((t) => t.length >= 3 && low.includes(t)))
      issues.push("avoid using your name");
    return issues;
  };

  // Full form check → sets fieldErrors and returns normalized values or false
  const validateForm = () => {
    const errs = {};
    const trimmedName = (name || "").trim();

    if (!trimmedName) errs.name = "Please enter your full name.";
    if (!isValidEmail(email)) errs.email = "Enter a valid email address.";

    const phoneRes = normalizePhone(phone);
    if (!phoneRes.ok) errs.phone = phoneRes.reason;

    const pwIssues = passwordIssues(password, trimmedName, email);
    if (pwIssues.length)
      errs.password = "Password must include: " + pwIssues.join(", ") + ".";
    if (confirmPassword !== password)
      errs.confirmPassword = "Passwords do not match.";

    if (!companyRole) errs.companyRole = "Select your role.";
    if (!referralSource) errs.referralSource = "Select an option.";

    // NEW: require agreement to terms
    if (!agreed)
      errs.agreed = "Please accept the Terms and Privacy Policy to continue.";

    setFieldErrors(errs);
    if (Object.keys(errs).length) return false;

    return { name: trimmedName, phoneE164: phoneRes.e164 };
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null); // clear global banner
    setLoading(true);
    trackEvent("sign_up_attempt", {
      page: "register",
    });

    try {
      // Clear previous field errors before validating
      setFieldErrors({});

      const res = validateForm();
      if (!res) {
        trackEvent("sign_up_validation_failed", {
          fields: Object.keys(fieldErrors),
        });

        setLoading(false);
        return;
      }

      // This will throw if the email is already in use or other auth issue
      await registerUser(
        email,
        password,
        "employer",
        res.name,
        res.phoneE164, // store normalized phone
        companyRole,
        referralSource,
      );
      trackEvent("sign_up_success", {
        role: "employer",
        company_role: companyRole,
        referral_source: referralSource,
      });

      // Save the profile fields so the verify page can finish setup
      sessionStorage.setItem(
        "pendingEmployerProfile",
        JSON.stringify({
          name: res.name,
          email,
          phone: res.phoneE164,
          companyRole,
          referralSource,
        }),
      );

      navigate("/verify-email"); // <-- go to the verify screen
    } catch (err) {
      console.error("Register error:", err);
      trackEvent("sign_up_error", {
        error_code: err.code || "unknown",
      });

      // Keep the nice inline field error for duplicate email
      if (err?.code === "auth/email-already-in-use") {
        setFieldErrors((prev) => ({
          ...prev,
          email:
            "An account already exists with this email. Try logging in or resetting your password.",
        }));
      }

      // Use shared formatter for the banner
      const { title, message } = formatError("register", err);
      setError({ title, message });
    } finally {
      setLoading(false);
    }
  };

  const pwScore = Math.max(0, 5 - passwordIssues(password, name, email).length);
  const pwLabel = ["Very weak", "Weak", "Fair", "Good", "Strong", "Excellent"][
    pwScore
  ];

  return (
    <div className="register-page">
      <div className="auth-card">
        <div className="auth-header">
          <img src={Oiiko} alt="Oiiko Logo" className="companylogin-logo" />
          <div className="stepper"></div>
          <h1 className="register-title">Create Your Oiiko Employer Account</h1>
          <p className="register-subtext">
            Create your employer account to manage your team, track time, and
            stay in control all in one place.
          </p>
        </div>

        {/* 🔹 Global registration error banner */}
        {error && (
          <StatusBanner
            type="error"
            title={error.title}
            message={error.message}
            onClose={() => setError(null)}
          />
        )}

        {loading ? (
          <div className="register-loading">
            <Lottie
              animationData={loadingAnimation}
              loop
              style={{ height: 200 }}
            />
            <p className="register-loading-text">Setting up your account...</p>
          </div>
        ) : (
          <form onSubmit={handleRegister} className="register-form">
            <div className="form-row two">
              <div className="form-group">
                <label>Who is setting up Oiiko?</label>
                <input
                  type="text"
                  placeholder="Enter your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={fieldErrors.name ? "input-error" : ""}
                  required
                />
                {fieldErrors.name && (
                  <div className="field-error">{fieldErrors.name}</div>
                )}
              </div>

              <div className="form-group">
                <label>Your Contact Number</label>
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="e.g., (555) 555-1234"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onBlur={() => {
                    const r = normalizePhone(phone);
                    if (r.ok) setPhone(r.national);
                  }}
                  className={fieldErrors.phone ? "input-error" : ""}
                  required
                />
                {fieldErrors.phone && (
                  <div className="field-error">{fieldErrors.phone}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Official Company Email</label>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={fieldErrors.email ? "input-error" : ""}
                required
              />
              {fieldErrors.email && (
                <div className="field-error">{fieldErrors.email}</div>
              )}
            </div>

            <div className="form-group">
              <label>Create a Secure Password</label>
              <div className="password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={fieldErrors.password ? "input-error" : ""}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-visibility1"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>

              {/* strength bar */}
              <div className={`pw-meter s-${pwScore}`}>
                <div
                  className="bar"
                  style={{ width: `${(pwScore / 5) * 100}%` }}
                />
                <span className="label">{pwLabel}</span>
              </div>

              {fieldErrors.password && (
                <div className="field-error">{fieldErrors.password}</div>
              )}
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <div className="password-field">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={fieldErrors.confirmPassword ? "input-error" : ""}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-visibility1"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                >
                  {showConfirmPassword ? "Hide" : "Show"}
                </button>
              </div>
              {fieldErrors.confirmPassword && (
                <div className="field-error">{fieldErrors.confirmPassword}</div>
              )}
            </div>

            <div className="form-row two">
              <div className="form-group">
                <label>Company Role</label>
                <select
                  value={companyRole}
                  onChange={(e) => setCompanyRole(e.target.value)}
                  required
                  className={`register-select ${
                    fieldErrors.companyRole ? "input-error" : ""
                  }`}
                >
                  <option value="">Select Role</option>
                  <option value="Owner">Owner</option>
                  <option value="Manager">Manager</option>
                  <option value="HR">HR</option>
                </select>
                {fieldErrors.companyRole && (
                  <div className="field-error">{fieldErrors.companyRole}</div>
                )}
              </div>

              <div className="form-group">
                <label>How did you hear about us?</label>
                <select
                  value={referralSource}
                  onChange={(e) => setReferralSource(e.target.value)}
                  required
                  className={`register-select ${
                    fieldErrors.referralSource ? "input-error" : ""
                  }`}
                >
                  <option value="">Select an option</option>
                  <option value="POS or payroll provider">
                    POS or payroll provider
                  </option>
                  <option value="Recommended">Recommended</option>
                  <option value="YouTube">YouTube</option>
                  <option value="In the news">In the news</option>
                  <option value="Social Media (Facebook, Instagram, etc.)">
                    Social Media (Facebook, Instagram, etc.)
                  </option>
                  <option value="Online search (Google, Bing, etc.)">
                    Online search (Google, Bing, etc.)
                  </option>
                  <option value="Online review or blog post">
                    Online review or blog post
                  </option>
                  <option value="Radio or podcast">Radio or podcast</option>
                  <option value="Mail flyer">Mail flyer</option>
                  <option value="Other">Other</option>
                </select>
                {fieldErrors.referralSource && (
                  <div className="field-error">
                    {fieldErrors.referralSource}
                  </div>
                )}
              </div>
            </div>

            {/* NEW: Terms & Privacy consent */}
            <div className="form-group terms-consent">
              <label className="checkbox-lines">
                <input
                  type="checkbox"
                  className="tiny-checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  aria-describedby="terms-help"
                />
                <span>
                  I agree to the{" "}
                  <a
                    href="/legal#responsibilities"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Use
                  </a>{" "}
                  and{" "}
                  <a href="/legal" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>
              <div id="terms-help" className="hint">
                Please accept the Terms and Privacy Policy to continue.
              </div>
              {fieldErrors.agreed && (
                <div className="field-error">{fieldErrors.agreed}</div>
              )}
            </div>

            <div className="actions-row">
              <button
                type="submit"
                className="btn-primary1"
                disabled={loading || !agreed}
                title={!agreed ? "Please agree to proceed" : undefined}
              >
                {loading ? "Creating..." : "Sign Up"}
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => navigate("/login")}
              >
                Back to login
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
